import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';

import { DateTime } from "luxon";
import { NavigationService } from 'projects/borrower-app/src/app/services/navigation.service';
import { LocalStorageService } from 'projects/borrower-app/src/app/services/local-storage.service';
import { AuthService } from 'projects/borrower-app/src/app/services/auth.service';
import { AuthData } from 'projects/borrower-app/src/app/models/auth/auth-data.model';
import { LoginStatus } from 'projects/borrower-app/src/app/models/auth/login-status.enum';
import { AuthenticationResponse } from 'projects/borrower-app/src/app/models/auth/auth-response.model';
import { UtilsService } from 'projects/borrower-app/src/app/services/utils.service';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Constants } from 'projects/borrower-app/src/app/services/constants';

const TOKEN_HEADER_KEY = "Bearer";

// Access tokens are short-lived (15 min). Refresh only once they are within ~2 minutes of expiry so a
// single browsing session doesn't trigger a refresh on essentially every request.
const TOKEN_EXPIRATION_TOLERANCE_IN_SECS = 120;

const TOKEN_EXPIRATION_ERROR = 'Token Expiration';

// Emitted in place of an auth response when the refresh call failed without rejecting the session.
const REFRESH_FAILED = Symbol('refreshFailed');

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private _isRefreshing = false;
  private _refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private readonly _localStorage: LocalStorageService,
    private readonly _navigationService: NavigationService,
    private readonly _utilsService: UtilsService,
    private readonly _authService: AuthService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Local static assets (e.g. transloco's ./assets/i18n/en.json) are public and
    // must bypass auth entirely. Otherwise a stale/expired token in localStorage
    // routes the request into the token-refresh path; when no valid refresh token
    // is available the request hangs on _refreshTokenSubject and never reaches the
    // network. The i18n file then never loads and the *transloco directive — which
    // only renders once the active language resolves — leaves the login UI blank.
    if (this.isLocalAssetRequest(request.url)) {
      return next.handle(request);
    }

    const authData = this._localStorage.authorizationData;
    const clientIp: {ip: string} = this._localStorage.getItem(Constants.clientIpKey);
    request = this.addClientIpHeader(request, clientIp?.ip);
    if (!request.url.includes('refreshToken')) {
      if (this.isTokenAboutToExpire(authData)) {
        return this.refreshToken(request, next);
      }
    }

    if (authData && authData.token) {
      request = this.addTokenHeader(request, authData.token);
    }

    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.error === TOKEN_EXPIRATION_ERROR) {
          return this.refreshToken(request, next);
        } else if (error.status === 401) {
          // A 401 that is not a plain token-expiration means the server-side session is gone (idle/absolute
          // timeout, explicit logout, or revocation). Drop the stale auth so it can't be replayed, then
          // send the user to login.
          if (this._localStorage.authorizationData) {
            this._localStorage.authorizationData = null;
          }
          this._navigationService.navigateToLogin();
        }
        error.currentPath = location.pathname;
        error.message = error?.error?.message || null;
        throw error;
      })
    );
  }

  private isLocalAssetRequest = (url: string): boolean => {
    // Relative paths into the app's own assets folder. API calls use the absolute
    // apiBaseUrl, so they never match these prefixes (an "/api/assets/..." path
    // starts with "/api/", not "/assets/").
    return url.startsWith('./assets/') || url.startsWith('assets/') || url.startsWith('/assets/');
  }

  private isTokenAboutToExpire = (authData: AuthData) => {
    if (authData && authData.expiresAt) {
      const expiresAt = DateTime.fromISO(authData.expiresAt.toString());
      const now = DateTime.fromJSDate(new Date());
      const secondsToExpiration = expiresAt.diff(now, 'seconds');
      return secondsToExpiration.seconds <= TOKEN_EXPIRATION_TOLERANCE_IN_SECS;
    }
    return false;
  }

  private addTokenHeader = (request: HttpRequest<any>, token: string) => {
    return request.clone({
      setHeaders: {
        Authorization: `${TOKEN_HEADER_KEY} ${token}`
      }
    });
  }

  private addClientIpHeader = (request: HttpRequest<any>, ipAddress: string) => {
    if (!ipAddress) {
      return request;
    }
    return request.clone({
      setHeaders: {
        'LE-ClientIP': ipAddress
      }
    });
  }

  private refreshToken = (request: HttpRequest<any>, next: HttpHandler) => {
    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._refreshTokenSubject.next(null);

      const refreshToken = this._localStorage.authorizationData.refreshToken;

      if (refreshToken) {
        return this._authService.refreshToken(refreshToken).pipe(
          // Only the refresh call's own failure falls back to sending the request. Once the refresh succeeds, the
          // request is sent once with the new token and its errors go back to the caller: re-sending it would
          // repeat a save that may already have committed, and its error would hide the original one (ENG-1961).
          catchError(error => {
            // Always clear the in-progress latch on a failed refresh: otherwise it stays true forever and
            // every subsequent request hangs waiting on _refreshTokenSubject (UI appears frozen). Clearing it
            // lets the next request start a fresh refresh, which also releases any requests queued here.
            this._isRefreshing = false;
            if (this.isSessionRejected(error)) {
              this.endSession();
              return throwError(error);
            }
            return of(REFRESH_FAILED);
          }),
          switchMap((authResponse: any) => {
            if (authResponse === REFRESH_FAILED) {
              return next.handle(request);
            }
            // A response missing either token is a failed refresh even when it arrives as a 200: persisting it
            // would replace the stored credentials with undefined and every later refresh would send
            // "refreshToken=undefined".
            if (authResponse?.loginStatus === LoginStatus.Error
              || !authResponse?.jwtToken
              || !authResponse?.refreshToken) {
              this._isRefreshing = false;
              this.endSession();
              return throwError(authResponse?.errorMessage);
            }
            this.persistAuthData(authResponse);
            this._isRefreshing = false;
            this._refreshTokenSubject.next(authResponse.jwtToken);

            return next.handle(this.addTokenHeader(request, authResponse.jwtToken)).pipe(
              catchError(error => {
                // The server rejected the session even with the fresh token: end it, as for any other request.
                if (this.isSessionRejected(error)) {
                  this.endSession();
                }
                return throwError(() => error);
              })
            );
          })
        );
      } else {
        this._navigationService.navigateToLogin();
      }
    }
    return this._refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  // A 401 that is not a plain token expiration means the server-side session is gone.
  private isSessionRejected = (error: any): boolean => {
    return error?.error !== TOKEN_EXPIRATION_ERROR && error?.status === 401;
  }

  // Drop the stale auth so it can't be replayed, then send the user to login.
  private endSession = (): void => {
    if (this._localStorage.authorizationData) {
      this._localStorage.authorizationData = null;
    }
    this._navigationService.navigateToLogin();
  }

  private persistAuthData = (authResponse: AuthenticationResponse): void => {
    let jwtPayload = this._utilsService.parseJwt(authResponse.jwtToken);
    const authData: AuthData = {
      companyGuid: this._localStorage.authorizationData.companyGuid,
      userCompanyGuid: authResponse.userCompanyGuid,
      token: authResponse.jwtToken,
      userName: authResponse.userName,
      refreshToken: authResponse.refreshToken,
      companies: authResponse.availableCompanies,
      rememberMe: this._localStorage.authorizationData.rememberMe,
      scope: this._localStorage.authorizationData.scope,
      expiresAt: this._utilsService.getTokenExpireDate(jwtPayload.exp)
    };
    this._localStorage.authorizationData = authData;
  }
}
